import {
  useCallback,
  useRef,
  useMemo,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Button,
  Space,
  Card,
  Steps,
  Spin,
  Table,
  message,
  Popconfirm,
  Modal,
  Checkbox,
  Radio,
  Form,
  Input,
  Divider,
  Upload,
  Image,
} from 'antd';
import {
  FilePdfOutlined,
  PrinterOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import {
  extractClipboardImageFiles,
  fileToUploadFile,
} from 'pages/rrs/recycling-consignment/modal-components/clipboard-image';
import {
  getBase64,
  uploadImageFiles,
} from 'pages/rrs/recycling-consignment/modal-components/image-utils';
import {
  CustomerInfoDefault,
  UserProductInfoDefault,
  ProductInfoDefault,
  LogisticsInfoDefault,
  ReturnGoodsDefault,
  CollectionDefault,
  OMS_RECYCLE_ORDER_STATUS_MAP,
  ShopMaping,
  consultingSource,
} from 'constants/RecyclingConsignment';
import useHandleDetailData from './useHandleDetailData';
import useMoreModal from 'pages/rrs/recycling-consignment/modal-components';
import { SelectOption } from 'types/base';
import LinkButton from 'components/link-button';
import ImageSliceShow from 'components/image-slice-show';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import getStoreTimezone from 'utils/getStoreTimezone';
import { StoreStatusOption } from 'constants/appointment-management';
import ModifyLogistics from './modify-logistics';
import ModifyBaseInfo from './modify-base-info';
import ModifyTimes from './modify-times';
import useResource from 'commons/hooks/useResource';
import MemberMailSend from 'pages/sys/member-mail-send';
import RecycleOrderPriorityTag from '../recycling-consignment/components/recycle-order-priority-tag';
import ModifyValuation from './modify-valuation';
import LayoutFloatButton from 'components/layout-float-button';
import ModifyPayment from './modify-payment';
import ModifyOperation from './modify-operation';
import LOCALS from '../../../commons/locals';
import { Trans } from 'react-i18next';
import i18n from '../../../i18n';
import SignatureCanvas from 'react-signature-canvas';
import { openSignedRecycleOrderContract } from 'utils/openSignedRecycleOrderContract';
import { thousands } from 'utils/tools';
import EditableText from 'components/editable-input';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { OmsRecycleOrderDetail } from 'types/oms';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import {
  omsOrderRecycleOrderUpdateInfo,
  deleteRecycleOrder,
  convertRecycleOrderToConsignment,
  restoreRecycleOrderConsignment,
  revertRecycleOrderToWaitPayment,
  cancelCompletedRecycleOrder,
  sendOmsRecycleOrderFundsTransferNotification,
  fetchOmsRecyclingList,
  downloadSignedContract,
} from 'apis/oms';
import OmsRecycleOrderEmail from './oms-recycle-order-email';
import commonApi from 'apis/common';
import {
  CircleDollarSign,
  Clock,
  ExternalLink,
  FileText,
  History,
  Info,
  Landmark,
  Mail,
  Package,
  Truck,
  User,
} from 'lucide-react';
import CopyButton from 'components/copy-button';
import AuthenticationResultCard from './authentication-result-card';
import { resolveReceptionistLabel } from './staff-utils';
import { EditableQuoteStaff, EditableQuoteTime } from './edit-quote-record';
import BindAuthenticationModal from './bind-authentication-modal';
import { SHOP_MAP } from 'constant/shop';
import ConsignmentAgreementEntry from './consignment-agreement-entry';
import ReceptionEntry from './reception-form/entry';

dayjs.extend(utc);
dayjs.extend(timezone);

/** 展示操作记录里附带的图片（存储 key 数组） */
const LogOperationImages = ({ imageKeys }: { imageKeys: string[] }) => {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    let isActive = true;
    const keys = imageKeys.filter(Boolean);

    Promise.all(
      keys.map((key) => commonApi.getSignedFileUrl(key).then((res) => res.url)),
    ).then((resolvedUrls) => {
      if (isActive) setUrls(resolvedUrls);
    });

    return () => {
      isActive = false;
    };
  }, [imageKeys]);

  if (urls.length === 0) return null;

  return (
    <Image.PreviewGroup>
      <div className="flex flex-wrap gap-2">
        {urls.map((url, index) => (
          <Image
            key={index}
            src={url}
            alt=""
            width={64}
            height={64}
            style={{ objectFit: 'cover' }}
          />
        ))}
      </div>
    </Image.PreviewGroup>
  );
};

const SignedContractDownloadButton = ({
  orderId,
  orderCode,
}: {
  orderId: string;
  orderCode?: string;
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await downloadSignedContract(orderId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `signed-contract-${orderCode || orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      message.error(String(i18n.t(LOCALS.signed_contract_download_failed)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="link"
      loading={loading}
      className="h-auto p-0"
      onClick={handleDownload}
    >
      {i18n.t(LOCALS.signed_contract_download_pdf)}
    </Button>
  );
};

const InfoList = ({
  items,
}: {
  items: {
    key: string;
    label: ReactNode;
    children: ReactNode;
  }[];
}) => {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
      {items.map((item) => {
        return (
          <div key={item.key}>
            <div className="text-sm font-medium text-gray-500">
              {item.label}
            </div>
            <div className="mt-1 text-sm font-medium whitespace-pre-line break-words">
              {item.children}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getTimeFormat = (date?: string): string => {
  if (!date) return '-';
  return dayjs(date).format('YYYY-MM-DD HH:mm');
};

const RecyclingConsignmentDetail = () => {
  const { id = '' } = useParams<{ id: string }>();
  /** Modal参数 */
  const modalOptionRef = useRef<any>({});
  const deleteRecycleOrderResource = useResource(
    'recycling-consignment-delete-order',
  );
  const canEditFinalPrice = useResource(
    'recycling-consignment-final-price-edit',
  );
  const canModifyBaseInfo = useResource(
    'recycling-consignment-modify-basic-info',
  );
  const { isAdmin } = useAppSelector(selectUserInfo);
  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);

  const [modifyLogisticsOpen, setModifyLogisticsOpen] = useState(false);
  const [modifyBaseInfoOpen, setModifyBaseInfoOpen] = useState(false);
  const [modifyTimesOpen, setModifyTimesOpen] = useState(false);
  const [modifyPaymentOpen, setModifyPaymentOpen] = useState(false);
  const [modifyOperationOpen, setModifyOperationOpen] = useState(false);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [bindAuthModalOpen, setBindAuthModalOpen] = useState(false);
  const [remarkForm] = Form.useForm();
  const [remarkTemplateChecked, setRemarkTemplateChecked] = useState(false);
  const [operationFileList, setOperationFileList] = useState<UploadFile[]>([]);
  const remarkHiddenPrefixRef = useRef<string>('');
  const [sendNotificationLoading, setSendNotificationLoading] = useState(false);
  const [convertToConsignmentLoading, setConvertToConsignmentLoading] =
    useState(false);
  const [restoreConsignmentLoading, setRestoreConsignmentLoading] =
    useState(false);
  const [revertToWaitPaymentLoading, setRevertToWaitPaymentLoading] =
    useState(false);
  const [cancelCompletedLoading, setCancelCompletedLoading] = useState(false);
  const [submissionOrderList, setSubmissionOrderList] = useState<
    OmsRecycleOrderDetail[]
  >([]);

  /** 详情相关数据 */
  const {
    loading,
    dataSource,
    currentStep,
    baseInfo,
    customerInfo,
    userProductInfo,
    productInfo,
    logisticsInfo,
    returnGoodsInfo,
    collectionInfo,
    logList,
    signedContractInfo,
    getDetail,
    authenticationDetail,
    isSupportedBagType,
  } = useHandleDetailData({ id });
  const receptionistName = useMemo(
    () =>
      resolveReceptionistLabel(
        staffSelectOptions,
        dataSource?.omsRecycleOrder?.receptionistId,
      ),
    [dataSource?.omsRecycleOrder?.receptionistId, staffSelectOptions],
  );
  /** 弹窗相关数据 */
  const {
    valuationModalElement,
    setValuationModalOpen,
    shiplableModalElement,
    setShiplableModalOpen,
    goodsReceivedModalElement,
    setGoodsReceivedModalOpen,
    finalValuationModalElement,
    setFinalValuationModalOpen,
    logisticsDocumentModalElement,
    setLogisticsDocumentModalOpen,
    updateFinalValuationModalElement,
    confirmSettlementModalElement,
    setConfirmSettlementModalOpen,
    confirmReturnModalElement,
    setConfirmReturnModalOpen,
    createPaymentVoucherModalElement,
    setPaymentVoucherModalOpen,
    cancelOrderModalElement,
    setCancelOrderModalOpen,
    deliverGoodsModalElement,
    setDeliverGoodsModalOpen,
    setCustomerAgreeValuationModalOpen,
    customerAgreeValuationModalElement,
    setPayInfoModalOpen,
    payInfoModalElement,
    completeModalElement,
    setCompleteModalOpen,
    consignmentToRecyclingModalElement,
    setConsignmentToRecyclingModalOpen,
  } = useMoreModal({
    dataSource,
    modalOption: modalOptionRef.current,
    getLoad: getDetail,
  });

  const [signedContractModalOpen, setSignedContractModalOpen] = useState(false);
  const [signedContractSubmitting, setSignedContractSubmitting] =
    useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  useEffect(() => {
    const submissionId = dataSource?.omsRecycleOrder?.submissionId;
    if (!submissionId) {
      setSubmissionOrderList([]);
      return;
    }

    let isActive = true;
    fetchOmsRecyclingList({
      pageNum: 0,
      pageSize: 100,
      submissionId,
    })
      .then((res) => {
        if (!isActive) return;
        setSubmissionOrderList(res.data?.list || []);
      })
      .catch(() => {
        if (!isActive) return;
        setSubmissionOrderList([]);
      });

    return () => {
      isActive = false;
    };
  }, [dataSource?.omsRecycleOrder?.submissionId]);

  const resolveOrderTypeLabel = (type?: number) => {
    if (type === 1) return '委託';
    if (type === 2) return '買取';
    return '';
  };

  const resolveOrderRank = (order: OmsRecycleOrderDetail) => {
    return order?.omsRecycleOrderProduct?.rank || '';
  };

  const resolveOrderProductId = (
    order: OmsRecycleOrderDetail,
    isCurrentOrder: boolean,
  ) => {
    if (isCurrentOrder && baseInfo.associatedProductId) {
      return baseInfo.associatedProductId;
    }
    return (
      order?.omsRecycleOrder?.productId ||
      order?.omsRecycleOrderItem?.productId ||
      order?.omsRecycleOrderProduct?.id ||
      ''
    );
  };

  const buildOrderProductSummary = (order: OmsRecycleOrderDetail) => {
    const currentOrderId = dataSource?.omsRecycleOrder?.id;
    const orderId = order?.omsRecycleOrder?.id;
    const isCurrentOrder = Boolean(
      currentOrderId && currentOrderId === orderId,
    );
    const orderType = order?.omsRecycleOrder?.type;
    const orderTypeLabel = resolveOrderTypeLabel(orderType);
    const productId = resolveOrderProductId(order, isCurrentOrder);
    const productRank = resolveOrderRank(order);
    const currency = order?.omsRecycleOrder?.currency || '';
    const rawPrice =
      orderType === 1
        ? order?.omsRecycleOrderItem?.finalSalePrice
        : order?.omsRecycleOrderItem?.finalRecyclePrice;
    const priceValue = thousands(rawPrice);
    const priceLabel = priceValue;
    const currencySummary =
      currency && priceLabel
        ? `${currency} ${priceLabel}`
        : currency || priceLabel;
    const priceSummary =
      orderTypeLabel && currencySummary
        ? `${orderTypeLabel}${currencySummary}`
        : currencySummary;

    return [productId, productRank, priceSummary].filter(Boolean).join('/');
  };

  const buildRemarkTemplate = () => {
    const orderType = dataSource?.omsRecycleOrder?.type;
    const orderTypeLabel = resolveOrderTypeLabel(orderType);
    const purchaseStaff = baseInfo.staffName || '';
    const submissionId = baseInfo.submissionId
      ? `${baseInfo.submissionId}`
      : '';
    const storeLabel = baseInfo.store || '';
    const relatedOrders =
      baseInfo.submissionId && submissionOrderList.length > 0
        ? submissionOrderList
        : dataSource?.omsRecycleOrder
          ? [dataSource]
          : [];
    const productSummary = relatedOrders
      .map((order) => buildOrderProductSummary(order))
      .filter((summary) => summary.length > 0)
      .join('\n');

    const hidden = [
      '*受付担当',
      receptionistName,
      '*仕入担当',
      purchaseStaff,
      '❶ 仕入番号（持込ID）',
      submissionId,
      '❷ 仕入方法（買取／委託）',
      orderTypeLabel,
      '❸ 受付店',
      storeLabel,
      '❹ 成約商品情報（商品番号／状態／価格）',
      productSummary,
    ].join('\n');

    const visible = [
      '❺ お客様の国・在住',
      '',
      '❻ 来店きっかけ（流入経路）',
      '',
      'AI検索 [Y/N] (If Y：GPT/Gemini/Grok等記入)',
      '',
      '❼ お客様SNS／連絡先',
      '',
      '❽ 詳細情報（年間成約1,000万以上必須：売却動機／職業等）',
      '',
      '❾ 担当者を追加済み（1000万JPY/50万HKD/8万SGD/23万AED以上）',
      '',
    ].join('\n');

    return { hidden, visible };
  };

  useEffect(() => {
    if (!remarkModalOpen) return;

    const onPaste = (e: globalThis.ClipboardEvent) => {
      const images = extractClipboardImageFiles(e);
      if (images.length === 0) return;

      // 有图片时优先走操作备注上传逻辑，避免把图片"粘贴"进页面其它地方
      e.preventDefault();

      // 追加到现有列表，而不是替换
      const newFiles = images.map((img) => fileToUploadFile(img));
      setOperationFileList((prev) => [...prev, ...newFiles]);
      message.success(
        i18n.t(LOCALS.payment_voucher_clipboard_paste_success, {
          count: images.length,
          mergeNotice: '',
        }),
      );
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [remarkModalOpen]);

  const handleOperationFileChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      // 允许多个文件，不再限制为最后一个
      const normalized = info.fileList.map((file) =>
        file.originFileObj ? { ...file, status: 'done' as const } : file,
      );
      setOperationFileList(normalized);
    },
    [],
  );

  const handleOperationFilePreview = useCallback(async (file: UploadFile) => {
    if (file.url) {
      window.open(file.url);
      return;
    }
    if (file.originFileObj) {
      const src = await getBase64(file.originFileObj as RcFile);
      const image = new window.Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow?.document.write(image.outerHTML);
    }
  }, []);

  const onRemarkTemplateChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    setRemarkTemplateChecked(checked);

    if (checked) {
      const { hidden, visible } = buildRemarkTemplate();
      remarkHiddenPrefixRef.current = hidden;
      remarkForm.setFieldValue('note', visible);
    } else {
      remarkHiddenPrefixRef.current = '';
      remarkForm.setFieldValue('note', '');
    }
  };

  /** 操作备注提交逻辑 */
  const onRemarkOk = useCallback(async () => {
    const data = await remarkForm.validateFields();
    const currentFileList = [...operationFileList];

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        let operationImagesParam: string | undefined;
        let operationImagesUrlParam: string | undefined;

        // 获取所有新上传的图片文件
        const imageFiles = currentFileList
          .filter((file) => file.originFileObj instanceof Blob)
          .map((file) => file.originFileObj as File);

        if (imageFiles.length > 0) {
          try {
            const { keys, urls } = await uploadImageFiles(imageFiles, id);
            operationImagesParam = keys;
            operationImagesUrlParam = urls;
          } catch {
            message.error(i18n.t(LOCALS.ai_identify_upload_failed));
            return;
          }
        }

        const hiddenPrefix = remarkHiddenPrefixRef.current;
        const noteWithPrefix =
          remarkTemplateChecked && hiddenPrefix
            ? `${hiddenPrefix}\n${data.note ?? ''}`
            : data.note;

        await omsOrderRecycleOrderUpdateInfo({
          omsRecycleOrderId: id,
          note: noteWithPrefix,
          operationImages: operationImagesParam,
          operationImagesUrl: operationImagesUrlParam,
          isSendNotice: remarkTemplateChecked || undefined,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setRemarkModalOpen(false);
        remarkForm.resetFields();
        setRemarkTemplateChecked(false);
        remarkHiddenPrefixRef.current = '';
        setOperationFileList([]);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id, remarkForm, remarkTemplateChecked, operationFileList]);

  const handleSendFundsTransferNotification = useCallback(async () => {
    if (!id) return;

    let remarkValue = '';

    Modal.confirm({
      title: i18n.t(LOCALS.send_transfer_notice),
      width: 500,
      content: (
        <div className="space-y-4 mb-4">
          <div>
            <div className="text-gray-500 mb-2 font-medium">
              {i18n.t(LOCALS.remark)}：
            </div>
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 4, maxRows: 6 }}
              maxLength={200}
              showCount
              defaultValue=""
              onChange={(e) => {
                remarkValue = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      okText: i18n.t(LOCALS.confirm) as string,
      cancelText: i18n.t(LOCALS.cancel) as string,
      onOk: async () => {
        try {
          setSendNotificationLoading(true);
          await sendOmsRecycleOrderFundsTransferNotification(
            id,
            remarkValue || undefined,
          );
          message.success(i18n.t(LOCALS.successful_operation));
        } catch (error) {
          // Request layer will surface error details; swallow to avoid unhandled rejection.
        } finally {
          setSendNotificationLoading(false);
        }
      },
    });
  }, [id]);

  const handleConvertToConsignment = useCallback(async () => {
    if (!id) return;
    setConvertToConsignmentLoading(true);
    try {
      await convertRecycleOrderToConsignment(id);
      message.success(i18n.t(LOCALS.successful_operation));
      getDetail();
    } finally {
      setConvertToConsignmentLoading(false);
    }
  }, [id, getDetail]);

  const handleRevertToWaitPayment = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: i18n.t(LOCALS.revert_consignment_to_wait_payment) as string,
      content: i18n.t(
        LOCALS.revert_consignment_to_wait_payment_confirm,
      ) as string,
      okText: i18n.t(LOCALS.confirm) as string,
      cancelText: i18n.t(LOCALS.cancel) as string,
      onOk: async () => {
        setRevertToWaitPaymentLoading(true);
        try {
          await revertRecycleOrderToWaitPayment(id);
          message.success(i18n.t(LOCALS.successful_operation));
          getDetail();
        } finally {
          setRevertToWaitPaymentLoading(false);
        }
      },
    });
  }, [id, getDetail]);

  const handleCancelCompleted = useCallback(() => {
    if (!id) return;
    let cancelMode: 1 | 2 | undefined;
    let remark = '';
    Modal.confirm({
      title: i18n.t(LOCALS.cancel_completed_recycle_order) as string,
      width: 480,
      content: (
        <div>
          <div className="mb-3">
            {i18n.t(LOCALS.cancel_completed_recycle_order_confirm) as string}
          </div>
          <Radio.Group
            onChange={(e) => {
              cancelMode = e.target.value;
            }}
          >
            <Radio value={1}>
              {i18n.t(LOCALS.cancel_completed_recycle_order_return) as string}
            </Radio>
            <Radio value={2}>
              {i18n.t(LOCALS.cancel_completed_recycle_order_direct) as string}
            </Radio>
          </Radio.Group>
          <Input.TextArea
            className="mt-3"
            maxLength={255}
            placeholder={i18n.t(LOCALS.cancellation_reason) as string}
            onChange={(e) => {
              remark = e.target.value;
            }}
          />
        </div>
      ),
      okText: i18n.t(LOCALS.confirm) as string,
      cancelText: i18n.t(LOCALS.cancel) as string,
      onOk: async () => {
        if (!cancelMode) {
          message.warning(
            i18n.t(
              LOCALS.cancel_completed_recycle_order_mode_required,
            ) as string,
          );
          return Promise.reject();
        }
        setCancelCompletedLoading(true);
        try {
          await cancelCompletedRecycleOrder(id, {
            cancelMode,
            remark: remark.trim() || undefined,
          });
          message.success(i18n.t(LOCALS.successful_operation));
          getDetail();
        } finally {
          setCancelCompletedLoading(false);
        }
      },
    });
  }, [id, getDetail]);

  const handleRestoreConsignment = useCallback(() => {
    if (!id) return;
    let republish = false;
    Modal.confirm({
      title: i18n.t(LOCALS.restore_consignment) as string,
      content: (
        <div>
          <div className="mb-3">
            {i18n.t(LOCALS.restore_consignment_confirm) as string}
          </div>
          <Checkbox
            defaultChecked={false}
            onChange={(e: CheckboxChangeEvent) => {
              republish = e.target.checked;
            }}
          >
            {i18n.t(LOCALS.restore_consignment_republish) as string}
          </Checkbox>
        </div>
      ),
      okText: i18n.t(LOCALS.confirm) as string,
      cancelText: i18n.t(LOCALS.cancel) as string,
      onOk: async () => {
        setRestoreConsignmentLoading(true);
        try {
          await restoreRecycleOrderConsignment(id, { republish });
          message.success(i18n.t(LOCALS.successful_operation));
          getDetail();
        } finally {
          setRestoreConsignmentLoading(false);
        }
      },
    });
  }, [id, getDetail]);

  const handleSignedContractSubmit = useCallback(async () => {
    const signature = signatureRef.current;
    if (!signature || signature.isEmpty()) {
      message.warning(
        String(i18n.t(LOCALS.signed_contract_signature_required)),
      );
      return;
    }

    const orderId = dataSource?.omsRecycleOrder?.id;
    if (!orderId) return;

    setSignedContractSubmitting(true);
    try {
      openSignedRecycleOrderContract({
        orderIdList: [orderId],
        signatureImage: signature.getTrimmedCanvas().toDataURL('image/png'),
      });
    } catch (error) {
      console.error('Error generating signed contract PDF:', error);
      message.error(String(i18n.t(LOCALS.signed_contract_generate_pdf_failed)));
    } finally {
      setSignedContractSubmitting(false);
    }
  }, [dataSource]);

  /** 操作按钮 */
  const renderOption = useCallback(() => {
    if (!dataSource) return;
    const orderStatus = dataSource?.omsRecycleOrder?.status || 0;
    const stateWrapper = dataSource?.omsRecycleOrderLogistics?.stateWrapper;
    const shippingDocument =
      dataSource?.omsRecycleOrderLogistics?.shippingDocument;
    const cancelType = dataSource?.omsRecycleOrder?.cancelType;
    const appointmentId = dataSource.omsRecycleOrder?.appointmentId;
    const isSaleToRecycle = dataSource.omsRecycleOrder?.isSaleToRecycle;
    const createdFrom = consultingSource.findIndex(
      (d) => d.value === dataSource.omsRecycleOrder?.createdFrom,
    );
    const stateWrapperShippingDocument: string[] = JSON.parse(
      dataSource?.omsRecycleOrderLogistics?.stateWrapperShippingDocument ||
        '[]',
    );

    return (
      <Space wrap style={{ width: '100%' }}>
        {/* 初步估值 状态为【1-待初步估值】才有该按钮 */}
        {(orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_FIRST_VALUATION ||
          orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.VALUATION_EXPIRED) && (
          <Button
            onClick={() => {
              setValuationModalOpen(true);
            }}
          >
            {orderStatus ===
              OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_FIRST_VALUATION &&
              i18n.t(LOCALS.initial_appraisal_o)}
            {orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.VALUATION_EXPIRED &&
              i18n.t(LOCALS.re_evaluate)}
          </Button>
        )}
        {/* 2-客户同意初步报价 */}
        {orderStatus ===
          OMS_RECYCLE_ORDER_STATUS_MAP.HAS_BEEN_FIRST_VALUATION &&
          createdFrom > -1 && (
            <Button
              onClick={() => {
                setDeliverGoodsModalOpen(true);
              }}
            >
              选择发货
            </Button>
          )}
        {/* 状态为【3-待收货(2-客户同意初步报价)】 */}
        {[3].includes(orderStatus as number) && (
          <>
            {/* 上传shiplable：只有香港能上传 shippinglabel */}
            {dataSource?.omsRecycleOrder?.storeId === 2 && (
              <Button
                onClick={() => {
                  setShiplableModalOpen(true);
                }}
              >
                {dataSource?.omsRecycleOrderLogistics?.shippingLabel
                  ? '查看Shiplable'
                  : i18n.t(LOCALS.upload_shiplable)}
              </Button>
            )}
            {/* 确认收货：上传了邮寄凭证 */}
            {(shippingDocument || appointmentId) && (
              <Button
                onClick={() => {
                  setGoodsReceivedModalOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.confirm_receipt} />
              </Button>
            )}
            {/* 包装材料:1.需要，0：不需要 */}
            {stateWrapper === 1 && (
              <Button
                onClick={() => {
                  setLogisticsDocumentModalOpen(true);
                }}
              >
                {stateWrapperShippingDocument.length
                  ? i18n.t(LOCALS.view_packaging_materials_certificate)
                  : i18n.t(LOCALS.upload_packing_slip)}
              </Button>
            )}
          </>
        )}
        {/* 状态为【4-待最终报价】 */}
        {orderStatus === 4 && (
          <div>
            <Button
              onClick={() => {
                setFinalValuationModalOpen(true);
              }}
            >
              {i18n.t(LOCALS.final_quote)}
            </Button>
          </div>
        )}
        {/* 5-待客户确认 */}
        {orderStatus ===
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_AGREE_FINAL_VALUATION &&
          createdFrom > -1 && (
            <div>
              <Button
                onClick={() => {
                  setCustomerAgreeValuationModalOpen(true);
                }}
              >
                同意最终报价
              </Button>
            </div>
          )}
        {/* 5-待客户确认，6-寄卖中 
            【8-待退货 + 5用户拒绝最终报价】= 用户拒绝最终报价
            */}
        {(orderStatus === 8 && cancelType === 5) ||
        [6].includes(orderStatus as number) ? (
          <>
            <div>
              {/* <Button
                onClick={() => {
                  modalOptionRef.current = {
                    UpdateFinalValuation: {
                      status: orderStatus,
                    },
                  };
                  setUpdateFinalValuationModalOpen(true);
                }}
              >
                {i18n.t(LOCALS.modify_quote)}
              </Button> */}
            </div>
          </>
        ) : null}

        {(orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.ON_SALE ||
          (!!isSaleToRecycle &&
            [
              OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_FINAL_VALUATION,
              OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_AGREE_FINAL_VALUATION,
            ].includes(orderStatus as number))) && (
          <>
            <div>
              <Button
                onClick={() => {
                  setConfirmSettlementModalOpen(true);
                }}
              >
                {i18n.t(LOCALS.confirm_settlement)}
              </Button>
            </div>
          </>
        )}

        {/* 7-待打款 */}
        {[
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_PAYMENT,
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_PAYMENT_RECEIVED,
        ].includes(orderStatus as number) && (
          <Button
            loading={sendNotificationLoading}
            onClick={handleSendFundsTransferNotification}
            style={{
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              color: 'white',
            }}
          >
            {i18n.t(LOCALS.send_transfer_notice)}
          </Button>
        )}
        {orderStatus === 7 && (
          <>
            {createdFrom > -1 && (
              <Button
                onClick={() => {
                  setPayInfoModalOpen(true);
                }}
              >
                填写打款信息
              </Button>
            )}
          </>
        )}
        {/* 8-待退货 */}
        {orderStatus === 8 && (
          <Button
            onClick={() => {
              setConfirmReturnModalOpen(true);
            }}
          >
            {i18n.t(LOCALS.confirm_return)}
          </Button>
        )}
        {/* 待退货 / 已确认退货（已取消）+ 后台取消的寄卖单：恢复寄卖（仅 admin） */}
        {isAdmin &&
          [
            OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_RETURN_GOODS,
            OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_RETURN_GOODS_RECEIVED,
            OMS_RECYCLE_ORDER_STATUS_MAP.RETURN_GOODS_RECEIVED,
          ].includes(orderStatus) &&
          dataSource.omsRecycleOrder?.type ===
            OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT &&
          cancelType === 4 && (
            <Button
              loading={restoreConsignmentLoading}
              onClick={handleRestoreConsignment}
            >
              {i18n.t(LOCALS.restore_consignment)}
            </Button>
          )}
        {/* 交易完成的回收单：取消（仅 admin） */}
        {isAdmin &&
          orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.COMPLETE &&
          dataSource.omsRecycleOrder?.type ===
            OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE && (
            <Button
              danger
              type="primary"
              loading={cancelCompletedLoading}
              onClick={handleCancelCompleted}
            >
              {i18n.t(LOCALS.cancel_completed_recycle_order)}
            </Button>
          )}
        {/* 交易完成的寄卖单：打款失败退回待打款（仅 admin） */}
        {isAdmin &&
          orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.COMPLETE &&
          dataSource.omsRecycleOrder?.type ===
            OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT && (
            <Button
              loading={revertToWaitPaymentLoading}
              onClick={handleRevertToWaitPayment}
            >
              {i18n.t(LOCALS.revert_consignment_to_wait_payment)}
            </Button>
          )}
        {/* 取消订单-寄卖中之前的都有取消订单 */}
        {[1, 2, 3, 4, 5, 6].includes(orderStatus as number) && (
          <Button
            onClick={() => setCancelOrderModalOpen(true)}
            danger
            type="primary"
          >
            {i18n.t(LOCALS.cancel_order)}
          </Button>
        )}
        {/* 13-表示已打款，但是还没确认收款 */}
        {orderStatus === 13 && (
          <Button
            onClick={() => {
              modalOptionRef.current = {
                PaymentVoucher: {
                  type: 'update',
                },
              };
              setPaymentVoucherModalOpen(true);
            }}
          >
            重新上传凭证
          </Button>
        )}
        {[
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_FIRST_VALUATION,
          OMS_RECYCLE_ORDER_STATUS_MAP.HAS_BEEN_FIRST_VALUATION,
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_GOODS_RECEIVED,
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_FINAL_VALUATION,
          OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_AGREE_FINAL_VALUATION,
          OMS_RECYCLE_ORDER_STATUS_MAP.VALUATION_EXPIRED,
        ].includes(orderStatus) && (
          <Button
            onClick={() => {
              setCompleteModalOpen(true);
            }}
          >
            {i18n.t('one_click_completion')}
          </Button>
        )}
        {
          <>
            <Button
              onClick={() => {
                window.open(
                  `/prints/oms-recycle-order-contract?orderIdList=${dataSource.omsRecycleOrder?.id}`,
                );
              }}
              icon={<PrinterOutlined className="w-4 h-4" />}
            >
              {i18n.t('yYqvdvcMFh')}
            </Button>
          </>
        }
        <Button
          onClick={() => {
            signatureRef.current?.clear();
            setSignedContractModalOpen(true);
          }}
          icon={<FilePdfOutlined className="w-4 h-4" />}
        >
          {i18n.t(LOCALS.signed_contract_generate_pdf)}
        </Button>
        <Button
          onClick={() => {
            window.open(
              `/prints/oms-recycle-order-contract?orderIdList=${dataSource.omsRecycleOrder?.id}&contractType=return`,
            );
          }}
          icon={<PrinterOutlined className="w-4 h-4" />}
        >
          {i18n.t('udIpUVuvzQ')}
        </Button>
        {dataSource?.omsRecycleOrder?.createdFrom !== undefined &&
        (dataSource?.omsRecycleOrder?.createdFrom === SHOP_MAP.GINZA ||
          dataSource?.omsRecycleOrder?.createdFrom === SHOP_MAP.UMEDA) ? (
          <>
            <Button
              onClick={() => {
                window.open(
                  `/prints/oms-recycle-order-contract?orderIdList=${dataSource.omsRecycleOrder?.id}&contractType=settlement`,
                );
              }}
            >
              {i18n.t(LOCALS.settlement_statement)}
            </Button>
          </>
        ) : null}
        {OMS_RECYCLE_ORDER_STATUS_MAP.ON_SALE === orderStatus && (
          <Button onClick={() => setConsignmentToRecyclingModalOpen(true)}>
            {i18n.t('consignment_to_recycling')}
          </Button>
        )}
        {/* 绑定鉴定结果 */}
        <Button onClick={() => setBindAuthModalOpen(true)}>
          {i18n.t(LOCALS.bind_authentication_result)}
        </Button>
        {/* 操作备注按钮 */}
        <Button
          onClick={() => {
            setRemarkModalOpen(true);
          }}
        >
          <Trans i18nKey={LOCALS.operation_remark} />
        </Button>
        {orderStatus === 7 && (
          <Button
            style={{ color: '#1677ff', borderColor: '#1677ff' }}
            onClick={() => {
              setPaymentVoucherModalOpen(true);
              modalOptionRef.current = {
                PaymentVoucher: {
                  type: 'create',
                },
              };
            }}
          >
            {i18n.t(LOCALS.upload_payment_proof)}
          </Button>
        )}
        {isAdmin &&
          dataSource.omsRecycleOrder?.type ===
            OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE &&
          orderStatus === OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_PAYMENT && (
            <Popconfirm
              title={i18n.t(LOCALS.recycling_to_consignment)}
              description={
                <div className="w-[240px]">
                  {i18n.t(LOCALS.recycling_to_consignment_confirm)}
                </div>
              }
              onConfirm={handleConvertToConsignment}
            >
              <Button loading={convertToConsignmentLoading}>
                {i18n.t(LOCALS.recycling_to_consignment)}
              </Button>
            </Popconfirm>
          )}
        {/* 回收类型订单删除按钮 */}
        {deleteRecycleOrderResource && (
          <Popconfirm
            title={i18n.t('delete')}
            description={
              <div className="w-[150px]">
                {i18n.t('are_you_sure_to_delete')}
              </div>
            }
            onConfirm={async () => {
              await deleteRecycleOrder(id);
              getDetail();
            }}
          >
            <Button danger>{i18n.t('TlTJwaQjAw')}</Button>
          </Popconfirm>
        )}
      </Space>
    );
  }, [
    dataSource,
    sendNotificationLoading,
    convertToConsignmentLoading,
    restoreConsignmentLoading,
    revertToWaitPaymentLoading,
    cancelCompletedLoading,
    handleSendFundsTransferNotification,
    handleConvertToConsignment,
    handleRestoreConsignment,
    handleRevertToWaitPayment,
    handleCancelCompleted,
    deleteRecycleOrderResource,
    isAdmin,
    setValuationModalOpen,
    setDeliverGoodsModalOpen,
    setShiplableModalOpen,
    setGoodsReceivedModalOpen,
    setLogisticsDocumentModalOpen,
    setFinalValuationModalOpen,
    setCustomerAgreeValuationModalOpen,
    setConfirmSettlementModalOpen,
    setPayInfoModalOpen,
    setConfirmReturnModalOpen,
    setCancelOrderModalOpen,
    setPaymentVoucherModalOpen,
    setCompleteModalOpen,
    setConsignmentToRecyclingModalOpen,
    id,
    getDetail,
  ]);

  /* 基础信息 */
  const baseInfoEle = useMemo(() => {
    if (baseInfo) {
      const items = [
        {
          key: 'submissionId',
          label: <Trans i18nKey={LOCALS.sHXnOEVSrC} />,
          children: (
            <div className="flex items-center gap-2">
              <CopyButton copyText={baseInfo.submissionId?.toString() || ''}>
                {baseInfo.submissionId}
              </CopyButton>
              {baseInfo.submissionId && (
                <Link
                  className="hover:text-blue-500 text-black cursor-pointer flex items-center justify-center"
                  to={`/rrs/recycling-consignment-list?submissionId=${baseInfo.submissionId}&status=all`}
                >
                  <ExternalLink className="w-5 h-5" />
                </Link>
              )}
            </div>
          ),
        },
        {
          key: 'code',
          label: <Trans i18nKey={LOCALS.code} />,
          children: (
            <CopyButton copyText={baseInfo.code?.toString() || ''}>
              {baseInfo.code}
            </CopyButton>
          ),
        },
        {
          key: 'type',
          label: <Trans i18nKey={LOCALS.order_type} />,
          children: baseInfo.type,
        },
        {
          key: 'status',
          label: <Trans i18nKey={LOCALS.order_status} />,
          children: baseInfo.status,
        },
        {
          label: <Trans i18nKey={LOCALS.created_time} />,
          key: 'createTime',
          children: baseInfo.createTime,
        },
        {
          label: <Trans i18nKey={LOCALS.intention_confirmation_time} />,
          key: 'intentionTime',
          children: baseInfo.intentionTime,
        },
        {
          label: <Trans i18nKey={LOCALS.contract_confirmation_time} />,
          key: 'contractTime',
          children: baseInfo.contractTime,
        },
        {
          label:
            dataSource?.omsRecycleOrder?.phaseType === 5
              ? i18n.t(LOCALS.cancel_order_time)
              : i18n.t(LOCALS.transaction_completed_time),
          key: 'tradingTime',
          children: baseInfo.tradingTime,
        },
        {
          label: i18n.t(LOCALS.associated_sales_order),
          key: 'relatedSalesOrder',
          children: baseInfo.orderId ? (
            <LinkButton href={`/oms/order-view/${baseInfo.orderId}`}>
              {baseInfo.relatedSalesOrder}
            </LinkButton>
          ) : (
            '-'
          ),
        },
        {
          label: i18n.t(LOCALS.associated_product_id),
          key: 'associatedProductId',
          children: baseInfo.associatedProductId ? (
            <LinkButton
              href={`/pms/product-view/${baseInfo.associatedProductId}`}
            >
              {baseInfo.associatedProductId}
            </LinkButton>
          ) : (
            '-'
          ),
        },
        {
          label: i18n.t(LOCALS.channel),
          key: 'channel',
          children: baseInfo.channel,
        },
        /*  {
            label: i18n.t(LOCALS.created_by),
            key: 'createBy',
            children: baseInfo.createId ? (
              <LinkButton href={`/ums/member-view/${baseInfo.createId}`}>
                {baseInfo.createBy}
              </LinkButton>
            ) : (
              baseInfo.createBy
            ),
          },*/
        {
          label: i18n.t(LOCALS.shop_id),
          key: 'postStore',
          children: baseInfo.store,
        },
        {
          label: i18n.t(LOCALS.supplier_type),
          key: 'supplierType',
          children: baseInfo.supplierType || '-',
        },
        {
          label: i18n.t(LOCALS.priority),
          key: 'priority',
          children: (
            <RecycleOrderPriorityTag
              id={dataSource?.omsRecycleOrder?.id}
              priority={dataSource?.omsRecycleOrder?.priority}
              onSuccess={() => {
                getDetail();
              }}
            />
          ),
        },
        {
          label: i18n.t(LOCALS.reception_staff),
          key: 'receptionistName',
          children: receptionistName || '-',
        },
        {
          label: i18n.t(LOCALS.staff),
          key: 'staffName',
          children: baseInfo.staffName,
        },
        {
          label: i18n.t(LOCALS.manual_review_confirmer),
          key: 'reviewerName',
          children: dataSource?.omsRecycleOrder?.reviewerName || '-',
        },

        {
          key: 'isTradeIn',
          label: <Trans i18nKey={LOCALS.is_trade_in} />,
          children: baseInfo.isTradeIn,
        },
        {
          key: 'firstValuationShopRemark',
          label: <Trans i18nKey={LOCALS.service_comments} />,
          children: productInfo?.firstValuationShopRemark || '-',
        },
        {
          key: 'firstValuationStock',
          label: <Trans i18nKey={LOCALS.stock_availability} />,
          children: productInfo?.firstValuationStock || '-',
        },
        {
          key: 'firstValuationStockRemark',
          label: <Trans i18nKey={LOCALS.remark} />,
          children: productInfo?.firstValuationStockRemark || '-',
        },
      ];

      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-base font-medium">
              <Info className="w-5 h-5" />
              <Trans i18nKey={LOCALS.basic_info} />
            </div>
            <div className="flex items-center gap-2">
              {canModifyBaseInfo && (
                <Button
                  type="primary"
                  onClick={() => {
                    setModifyBaseInfoOpen(true);
                  }}
                >
                  {i18n.t(LOCALS.modify)}
                </Button>
              )}
              {isAdmin && (
                <Button
                  onClick={() => {
                    setModifyTimesOpen(true);
                  }}
                >
                  {i18n.t(LOCALS.modify_order_times)}
                </Button>
              )}
            </div>
          </div>
          <InfoList items={items} />
        </Card>
      );
    }
  }, [
    baseInfo,
    productInfo,
    dataSource?.omsRecycleOrder?.id,
    dataSource?.omsRecycleOrder?.phaseType,
    dataSource?.omsRecycleOrder?.priority,
    dataSource?.omsRecycleOrder?.reviewerName,
    getDetail,
    receptionistName,
    canModifyBaseInfo,
    isAdmin,
  ]);

  /* 客户信息 */
  const customerInfoEle = useMemo(() => {
    if (customerInfo) {
      const items = CustomerInfoDefault.map(
        ({ label, field }: { label: string; field: any }, i) => {
          return {
            key: field,
            label: label,
            children: (
              <>
                {field === 'memberId' ? (
                  <LinkButton
                    href={
                      customerInfo.memberId
                        ? `/ums/member-view/${customerInfo.memberId}`
                        : '#'
                    }
                  >
                    {customerInfo[field] || '-'}
                  </LinkButton>
                ) : (
                  customerInfo[field] || '-'
                )}
              </>
            ),
          };
        },
      );

      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-base font-medium">
              <User className="w-5 h-5" />
              <Trans i18nKey={LOCALS.customer_info} />
            </div>
            <div className="flex items-center gap-3">
              <ReceptionEntry
                orderId={id}
                storeId={dataSource?.omsRecycleOrder?.storeId}
                submissionId={dataSource?.omsRecycleOrder?.submissionId}
              />
              <ConsignmentAgreementEntry
                orderId={id}
                storeId={dataSource?.omsRecycleOrder?.storeId}
                submissionId={dataSource?.omsRecycleOrder?.submissionId}
                orderType={dataSource?.omsRecycleOrder?.type}
              />
            </div>
          </div>

          <InfoList items={items} />
        </Card>
      );
    }
  }, [
    customerInfo,
    dataSource?.omsRecycleOrder?.storeId,
    dataSource?.omsRecycleOrder?.submissionId,
    dataSource?.omsRecycleOrder?.type,
    id,
  ]);

  /* 用户提交商品信息 */
  const userProductInfoEle = useMemo(() => {
    if (userProductInfo) {
      const items = UserProductInfoDefault.map(
        ({ label, field }: { label: string; field: any }, i) => {
          const t = userProductInfo[field];
          return {
            key: field,
            label: label,
            children: (
              <>
                {field === 'picList' || field === 'storeReceiptPicsList' ? (
                  <ImageSliceShow imgList={t || []} />
                ) : (
                  <>{t || '-'}</>
                )}
              </>
            ),
          };
        },
      );

      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center gap-2 mb-4 text-base font-medium">
            <Package className="w-5 h-5" />
            <Trans i18nKey={LOCALS.user_submitted_product_info} />
          </div>
          <InfoList items={items} />
        </Card>
      );
    }
  }, [userProductInfo]);

  /* 商品实际信息 */
  const productInfoEle = useMemo(() => {
    if (productInfo) {
      const items = ProductInfoDefault.map(
        ({
          label,
          field,
          span = 1,
        }: {
          label: string;
          field: any;
          span?: number;
        }) => {
          const t = productInfo[field];
          return {
            key: field,
            label: label,
            span,
            children: (
              <>
                {['picList', 'info'].includes(field) ? (
                  <>
                    {field === 'picList' && (
                      <ImageSliceShow imgList={t || []} />
                    )}
                    {field === 'info' && (
                      <div className="grid grid-cols-2 gap-2">
                        {t &&
                          t.map((d: SelectOption, i: number) => (
                            <div className="flex items-center gap-2" key={i}>
                              <div className="text-gray-500 self-start">
                                {d.label}：
                              </div>
                              <div className="whitespace-pre-line">
                                {d.value}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                ) : (
                  <> {t || '-'}</>
                )}
              </>
            ),
          };
        },
      );
      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center gap-2 mb-4 text-base font-medium">
            <Package className="w-5 h-5" />
            <Trans i18nKey={LOCALS.genuine_product_info} />
          </div>
          <InfoList items={items} />
        </Card>
      );
    }
  }, [productInfo]);

  /* 估值信息 */
  const valuationInfoEle = useMemo(() => {
    const { omsRecycleOrderItem, omsRecycleOrder } = dataSource;
    if (!omsRecycleOrderItem || !omsRecycleOrder) return null;

    const { currency } = omsRecycleOrder;

    const {
      minRecyclePrice,
      maxRecyclePrice,
      minSalePrice,
      maxSalePrice,
      finalRecyclePrice,
      finalSalePrice,
      firstValuationTime,
      finalValuationTime,
      quoteStaffId,
      quoteTime,
    } = omsRecycleOrderItem;

    return (
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5" />
            <div className="text-base font-medium">
              {i18n.t(LOCALS.valuation_information)} {currency || 'JPY'}
            </div>
          </div>
          <ModifyValuation
            currency={currency}
            data={dataSource}
            onSuccess={() => {
              getDetail();
            }}
          />
        </div>
        <InfoList
          items={[
            {
              key: 'instant_sale',
              label: i18n.t(LOCALS.instant_sale),
              children:
                minRecyclePrice && maxRecyclePrice
                  ? `${thousands(minRecyclePrice)} ~ ${thousands(
                      maxRecyclePrice,
                    )}`
                  : '-',
            },
            {
              key: 'sale_price',
              label: i18n.t(LOCALS.consignment_preliminary_valuation),
              children:
                minSalePrice && maxSalePrice
                  ? `${thousands(minSalePrice)} ~ ${thousands(maxSalePrice)}`
                  : '-',
            },
            {
              key: 'initial_appraisal_time',
              label: i18n.t(LOCALS.initial_appraisal_time),
              children: getTimeFormat(firstValuationTime),
            },
            {
              key: 'finalRecyclePrice',
              label: i18n.t(LOCALS.recycling_confirmation_quote),
              children: finalRecyclePrice ? (
                <EditableText
                  value={finalRecyclePrice.toString()}
                  disabled={!canEditFinalPrice}
                  onChange={async (value) => {
                    if (!dataSource.omsRecycleOrder) return;
                    if (!dataSource.omsRecycleOrder.id) return;
                    await omsOrderRecycleOrderUpdateInfo({
                      omsRecycleOrderId: dataSource.omsRecycleOrder.id,
                      finalRecyclePrice: Number(value),
                    });
                    message.success(i18n.t(LOCALS.successful_operation));
                    setTimeout(() => {
                      window.location.reload();
                    }, 300);
                  }}
                />
              ) : (
                '-'
              ),
            },
            {
              key: 'finalSalePrice',
              label: i18n.t(LOCALS.consignment_confirmation_quote),
              children: finalSalePrice ? (
                <EditableText
                  value={finalSalePrice.toString()}
                  disabled={!canEditFinalPrice}
                  onChange={async (value) => {
                    if (!dataSource.omsRecycleOrder) return;
                    if (!dataSource.omsRecycleOrder.id) return;
                    await omsOrderRecycleOrderUpdateInfo({
                      omsRecycleOrderId: dataSource.omsRecycleOrder.id,
                      finalSalePrice: Number(value),
                    });
                    message.success(i18n.t(LOCALS.successful_operation));
                    setTimeout(() => {
                      window.location.reload();
                    }, 300);
                  }}
                />
              ) : (
                '-'
              ),
            },
            {
              key: 'quotation_confirmation_time',
              label: i18n.t(LOCALS.quotation_confirmation_time),
              children: getTimeFormat(finalValuationTime),
            },
            {
              key: 'quoteStaffId',
              label: i18n.t(LOCALS.quote_staff),
              children: dataSource.omsRecycleOrder?.id ? (
                <EditableQuoteStaff
                  orderId={String(dataSource.omsRecycleOrder.id)}
                  quoteStaffId={quoteStaffId}
                  staffSelectOptions={staffSelectOptions}
                  onSuccess={getDetail}
                />
              ) : (
                '-'
              ),
            },
            {
              key: 'quoteTime',
              label: i18n.t(LOCALS.quote_date),
              children: dataSource.omsRecycleOrder?.id ? (
                <EditableQuoteTime
                  orderId={String(dataSource.omsRecycleOrder.id)}
                  quoteTime={quoteTime}
                  onSuccess={getDetail}
                />
              ) : (
                '-'
              ),
            },
          ]}
        />
      </Card>
    );
  }, [dataSource, getDetail, canEditFinalPrice, staffSelectOptions]);

  // 预约信息
  const appointmentStoreInfoEle = useMemo(() => {
    const { omsAppointmentStoreRecord } = dataSource;

    if (!omsAppointmentStoreRecord) return null;

    const { storeId, status, code, beginTime, endTime } =
      omsAppointmentStoreRecord;

    if (!storeId || !beginTime || !endTime) {
      return null;
    }

    const statusText = StoreStatusOption.find((i) => i.value === status)?.label;

    return (
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex items-center gap-2 mb-4 text-base font-medium">
          <Clock className="w-5 h-5" />
          <Trans i18nKey={LOCALS.appointment_information} />
        </div>
        <InfoList
          items={[
            {
              key: 'shop',
              label: i18n.t(LOCALS.shop),
              children: <div>{storeId && ShopMaping[storeId]}</div>,
            },
            {
              key: 'appointment_time_slot',
              label: i18n.t(LOCALS.appointment_time_slot),
              children: (
                <div>
                  {dayjs(beginTime)
                    .tz(getStoreTimezone(storeId))
                    .format('YYYY-MM-DD')}{' '}
                  {dayjs(beginTime)
                    .tz(getStoreTimezone(storeId))
                    .format('HH:mm')}
                  -
                  {dayjs(endTime).tz(getStoreTimezone(storeId)).format('HH:mm')}
                </div>
              ),
            },
            {
              key: 'status',
              label: i18n.t(LOCALS.status),
              children: <div>{statusText}</div>,
            },
            {
              key: 'appointment_number',
              label: i18n.t(LOCALS.appointment_number),
              children: (
                <CopyButton copyText={code?.toString() || ''}>
                  {code}
                </CopyButton>
              ),
            },
          ]}
        />
      </Card>
    );
  }, [dataSource]);

  /* 物流信息 */
  const logisticsInfoEle = useMemo(() => {
    // RecycleOrderStatus > 2 &&
    if (logisticsInfo) {
      const items = LogisticsInfoDefault.map(
        ({ label, field }: { label: string; field: any }, i) => {
          const t = logisticsInfo[field];
          if (
            ['mailingInfoPhoto', 'cargoInfoPhoto', 'receivingPhoto'].includes(
              field,
            )
          ) {
            return {
              key: field,
              label: label,
              children: <ImageSliceShow imgList={t || []} />,
            };
          }
          return {
            key: field,
            label: label,
            children: <>{t || '-'}</>,
          };
        },
      );
      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <div className="text-base font-medium flex items-center gap-2">
                <span>{i18n.t(LOCALS.logistics_information)}</span>
                {dataSource?.omsRecycleOrderLogistics?.stateWrapper === 1 && (
                  <span className="text-red-500 text-xs">
                    *{i18n.t(LOCALS.packaging_materials_required)}
                  </span>
                )}
              </div>
            </div>
            <Button
              type="primary"
              onClick={() => {
                setModifyLogisticsOpen(true);
              }}
            >
              {i18n.t(LOCALS.modify_logistics_information)}
            </Button>
          </div>
          <InfoList items={items} />
        </Card>
      );
    }
  }, [logisticsInfo, dataSource]);

  /* 退货信息 */
  const returnGoodsInfoEle = useMemo(() => {
    // RecycleOrderStatus > 4 &&
    if (returnGoodsInfo) {
      const items = ReturnGoodsDefault.map(
        ({ label, field }: { label: string; field: any }, i) => {
          const t = returnGoodsInfo[field];
          if (field === 'returnVoucher') {
            return {
              key: field,
              label: label,
              children: <ImageSliceShow imgList={t || []} />,
            };
          }
          return {
            key: field,
            label: label,
            children: <> {t || '-'}</>,
          };
        },
      );

      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center gap-2 mb-4 text-base font-medium">
            <Truck className="w-5 h-5" />
            <Trans i18nKey={LOCALS.return_information} />
          </div>
          <InfoList items={items} />
        </Card>
      );
    }
  }, [returnGoodsInfo]);

  /* 收款信息 */
  const collectionInfoEle = useMemo(() => {
    // RecycleOrderStatus > 5 &&
    if (collectionInfo) {
      const items = CollectionDefault.map(
        ({
          label,
          field,
          span = 1,
        }: {
          label: string;
          field: any;
          span?: number;
        }) => {
          const t = collectionInfo[field];
          if (field === 'financialPaymentVoucher') {
            return {
              key: field,
              label: label,
              span,
              children: <ImageSliceShow imgList={t || []} />,
            };
          }
          if (field === 'financialPaymentInfo') {
            return {
              key: field,
              label: label,
              span,
              children: (
                <div className="grid grid-cols-2 gap-2">
                  {t &&
                    t.map((d: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="text-gray-500">{d.label}：</div>
                        <div>{d.value}</div>
                      </div>
                    ))}
                </div>
              ),
            };
          }

          return {
            key: field,
            label: label,
            span,
            children: <>{t || '-'}</>,
          };
        },
      );

      items.unshift({
        key: 'payment_details',
        label: i18n.t(LOCALS.payment_details),
        span: 3,
        children: <>{dataSource.omsRecycleOrder?.payoutDetail || '-'}</>,
      });

      return (
        <Card bodyStyle={{ padding: '16px' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-base font-medium">
              <Landmark className="w-5 h-5" />
              <span>{i18n.t(LOCALS.payment_information)}</span>
            </div>
            <Button
              type="primary"
              onClick={() => {
                setModifyPaymentOpen(true);
              }}
            >
              {i18n.t(LOCALS.update_payment_information)}
            </Button>
          </div>

          <InfoList items={items} />
        </Card>
      );
    }
  }, [collectionInfo, dataSource.omsRecycleOrder?.payoutDetail]);

  /* 上门签署合同 */
  const signedContractInfoEle = useMemo(() => {
    const orderId = dataSource.omsRecycleOrder?.id;
    if (
      !signedContractInfo?.signedContractSentTime &&
      !signedContractInfo?.signedContractFileKey
    ) {
      return null;
    }

    const items = [
      {
        key: 'signedContractSentTime',
        label: i18n.t(LOCALS.signed_contract_email_sent_time),
        children: signedContractInfo.signedContractSentTime
          ? getTimeFormat(signedContractInfo.signedContractSentTime)
          : '-',
      },
      {
        key: 'signedContractDownload',
        label: i18n.t(LOCALS.signed_contract_attachment),
        children:
          orderId && signedContractInfo.signedContractFileKey ? (
            <SignedContractDownloadButton
              orderId={orderId}
              orderCode={dataSource.omsRecycleOrder?.code}
            />
          ) : (
            '-'
          ),
      },
    ];

    return (
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex items-center gap-2 mb-4 text-base font-medium">
          <FileText className="w-5 h-5" />
          <span>{i18n.t(LOCALS.signed_contract_section_title)}</span>
        </div>
        <InfoList items={items} />
      </Card>
    );
  }, [
    signedContractInfo,
    dataSource.omsRecycleOrder?.id,
    dataSource.omsRecycleOrder?.code,
  ]);

  /* 操作记录 */
  const logListEle = useMemo(
    () => (
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-base font-medium">
            <History className="w-5 h-5" />
            <span>{i18n.t(LOCALS.operation_log)}</span>
          </div>
          <Button
            type="primary"
            onClick={() => {
              setModifyOperationOpen(true);
            }}
          >
            {i18n.t(LOCALS.modification_operation_record)}
          </Button>
        </div>
        <Table
          bordered
          tableLayout="auto"
          scroll={{ x: 'max-content' }}
          rowKey={'id'}
          pagination={false}
          dataSource={logList}
          columns={[
            {
              title: i18n.t(LOCALS.operation_time),
              dataIndex: 'time',
              key: 'time',
            },
            {
              title: i18n.t(LOCALS.operator),
              dataIndex: 'personnel',
              key: 'personnel',
            },
            {
              title: i18n.t(LOCALS.operation_content),
              dataIndex: 'remark',
              key: 'remark',
              width: 400,
              render: (text) => (
                <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
              ),
            },
            {
              title: <Trans i18nKey={LOCALS.related_images} />,
              dataIndex: 'operationImages',
              key: 'operationImages',
              render: (operationImages?: string[] | null) =>
                operationImages && operationImages.length > 0 ? (
                  <LogOperationImages imageKeys={operationImages} />
                ) : null,
            },
            {
              title: '状态变更',
              dataIndex: 'statusChage',
              key: 'statusChage',
            },
          ]}
        />
      </Card>
    ),
    [logList],
  );

  /** 步骤条显示 */
  const stepsEle = useMemo(() => {
    const step0 = {
      title: i18n.t(LOCALS.inquiry_order_progress),
    };
    const step1 = {
      title: i18n.t(LOCALS.intentional_order_progress),
    };
    const step2 = {
      title: i18n.t(LOCALS.contract_order_progress),
    };
    const step3_1 = {
      title: i18n.t(LOCALS.transaction_completed),
    };
    const step3_2 = {
      title: i18n.t(LOCALS.cancelled),
    };
    let items: any = [step0, step1, step2, step3_1];
    // 1-咨询取消，2-意向取消，3-合同取消
    const orderCancelType = dataSource.omsRecycleOrder?.orderCancelType;
    if (orderCancelType) {
      if (orderCancelType === 1) items = [step0, step3_2];
      if (orderCancelType === 2) items = [step0, step1, step3_2];
      if (orderCancelType === 3) items = [step0, step1, step2, step3_2];
    }
    return <Steps current={currentStep} items={items} />;
  }, [dataSource, currentStep]);

  return (
    <div>
      <Spin spinning={loading}>
        <div className="space-y-4">
          <Card bodyStyle={{ padding: '16px' }}>
            {stepsEle}
            <Divider className="my-4" />
            {renderOption()}
          </Card>

          {/* 基础信息 */}
          {baseInfoEle}
          {/* 客户信息 */}
          {customerInfoEle}
          {/* 用户提交商品信息 */}
          {userProductInfoEle}
          {/* 商品实际信息 */}
          {productInfoEle}
          {/* 已有 AI 鉴定任务仅展示结果，不再提供新建鉴定入口 */}
          {isSupportedBagType && authenticationDetail?.latestTask && (
            <AuthenticationResultCard
              data={authenticationDetail}
              omsRecycleOrderId={dataSource.omsRecycleOrder?.id}
              onUnbindSuccess={getDetail}
            />
          )}
          {/* 估值信息 */}
          {valuationInfoEle}
          {/* 预约信息 */}
          {appointmentStoreInfoEle}
          {/* 物流信息 */}
          {logisticsInfoEle}
          {/* 退货信息 */}
          {returnGoodsInfoEle}
          {/* 收款信息 */}
          {collectionInfoEle}
          {/* 签署合同 */}
          {signedContractInfoEle}
          {/* 操作记录 */}
          {logListEle}
          {/* 邮件历史 */}
          <OmsRecycleOrderEmail id={dataSource.omsRecycleOrder?.id} />
          {/* 发送邮件 */}
          <Card bodyStyle={{ padding: '16px' }}>
            <div className="flex items-center gap-2 mb-4 text-base font-medium">
              <Mail className="w-5 h-5" />
              <Trans i18nKey={LOCALS.send_email} />
            </div>
            <MemberMailSend
              memberId={dataSource.omsRecycleOrder?.memberId}
              relatedId={dataSource.omsRecycleOrder?.id}
              relatedType="recycle"
            />
          </Card>
        </div>

        {/* 合同信息 */}
        {/* {contractListEle} */}

        <LayoutFloatButton />
      </Spin>

      {/* -------下面是Modal弹窗-------- */}

      {/* 提交初步估值 */}
      {valuationModalElement()}

      {/* 上传shiplable */}
      {shiplableModalElement()}

      {/* 确认收货 */}
      {goodsReceivedModalElement()}

      {/* 最终报价 */}
      {finalValuationModalElement()}

      {/* 上传物流凭证 */}
      {logisticsDocumentModalElement()}

      {/* 修改报价 */}
      {updateFinalValuationModalElement()}

      {/* 确认结算 */}
      {confirmSettlementModalElement()}

      {/* 确认退货 */}
      {confirmReturnModalElement()}

      {/* 打款信息 */}
      {createPaymentVoucherModalElement()}

      {/* 取消订单 */}
      {cancelOrderModalElement()}

      {/* 意向订单-选择发货 */}
      {deliverGoodsModalElement()}

      {/* 意向订单-同意最终报价 */}
      {customerAgreeValuationModalElement()}

      {/* 意向订单-付款信息 */}
      {payInfoModalElement()}

      {/* 一键完成 */}
      {completeModalElement()}

      {/* 寄卖转回收 */}
      {consignmentToRecyclingModalElement()}

      <Modal
        open={signedContractModalOpen}
        title={i18n.t(LOCALS.signed_contract_generate_pdf)}
        width={960}
        onCancel={() => {
          if (signedContractSubmitting) return;
          setSignedContractModalOpen(false);
          signatureRef.current?.clear();
        }}
        footer={
          <>
            <Button
              onClick={() => signatureRef.current?.clear()}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.signed_contract_clear_signature)}
            </Button>
            <Button
              onClick={() => {
                if (signedContractSubmitting) return;
                setSignedContractModalOpen(false);
                signatureRef.current?.clear();
              }}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.cancel)}
            </Button>
            <Button
              type="primary"
              loading={signedContractSubmitting}
              onClick={handleSignedContractSubmit}
            >
              {i18n.t(LOCALS.signed_contract_preview_and_send)}
            </Button>
          </>
        }
      >
        <div className="mb-2 font-medium">
          {i18n.t(LOCALS.signed_contract_customer_signature)}
        </div>
        <div className="border border-gray-300 rounded bg-white overflow-hidden">
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            backgroundColor="white"
            canvasProps={{
              width: 900,
              height: 260,
              className: 'block w-full h-[260px]',
            }}
          />
        </div>
        <div className="mt-2 text-gray-500">
          {i18n.t(LOCALS.signed_contract_signature_hint)}
        </div>
      </Modal>

      {modifyLogisticsOpen && (
        <ModifyLogistics
          open={modifyLogisticsOpen}
          onCancel={() => {
            setModifyLogisticsOpen(false);
          }}
          onSuccess={() => {
            setModifyLogisticsOpen(false);
            getDetail();
          }}
          data={dataSource}
        />
      )}

      {modifyBaseInfoOpen && canModifyBaseInfo && (
        <ModifyBaseInfo
          open={modifyBaseInfoOpen}
          onCancel={() => {
            setModifyBaseInfoOpen(false);
          }}
          onSuccess={() => {
            setModifyBaseInfoOpen(false);
            getDetail();
          }}
          data={dataSource}
          staffSelectOptions={staffSelectOptions}
        />
      )}

      {modifyTimesOpen && isAdmin && dataSource && (
        <ModifyTimes
          open={modifyTimesOpen}
          onCancel={() => {
            setModifyTimesOpen(false);
          }}
          onSuccess={() => {
            setModifyTimesOpen(false);
            getDetail();
          }}
          data={dataSource}
        />
      )}

      <ModifyPayment
        open={modifyPaymentOpen}
        onCancel={() => {
          setModifyPaymentOpen(false);
        }}
        onSuccess={() => {
          setModifyPaymentOpen(false);
          getDetail();
        }}
        dataSource={dataSource}
      />

      <ModifyOperation
        open={modifyOperationOpen}
        onCancel={() => {
          setModifyOperationOpen(false);
        }}
        onSuccess={() => {
          setModifyOperationOpen(false);
          getDetail();
        }}
        dataSource={dataSource}
      />

      {/* 操作备注Modal */}
      <Modal
        open={remarkModalOpen}
        title={<Trans i18nKey={LOCALS.operation_remark} />}
        onCancel={() => {
          setRemarkModalOpen(false);
          remarkForm.resetFields();
          setRemarkTemplateChecked(false);
          remarkHiddenPrefixRef.current = '';
          setOperationFileList([]);
        }}
        onOk={onRemarkOk}
        width={700}
      >
        <Form form={remarkForm} labelCol={{ span: 4 }}>
          <Form.Item
            name="note"
            label={<Trans i18nKey={LOCALS.remark} />}
            rules={[{ required: true }]}
          >
            <Input.TextArea
              autoSize={
                remarkTemplateChecked ? { minRows: 16, maxRows: 24 } : undefined
              }
              rows={remarkTemplateChecked ? undefined : 4}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          {remarkTemplateChecked && (
            <div className="ml-[16.66%] -mt-4 mb-4 text-xs text-gray-400">
              <Trans i18nKey={LOCALS.remark_template_default_filled_hint} />
            </div>
          )}

          {/* 图片上传区域：支持多选上传、复制/粘贴上传，提交时多张图片会自动合成为一张 */}
          <Form.Item label={<Trans i18nKey={LOCALS.related_images} />}>
            <Upload
              accept="image/*"
              listType="picture-card"
              beforeUpload={() => false}
              fileList={operationFileList}
              onChange={handleOperationFileChange}
              onPreview={handleOperationFilePreview}
              multiple
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              <UploadOutlined />
            </Upload>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 6 }}>
              {i18n.t(LOCALS.operation_remark_upload_tip)}
            </div>
          </Form.Item>
        </Form>
        <div className="mt-2">
          <Checkbox
            checked={remarkTemplateChecked}
            onChange={onRemarkTemplateChange}
          >
            <Trans i18nKey={LOCALS.customer_info_record_template} />
          </Checkbox>
        </div>
      </Modal>

      {dataSource?.omsRecycleOrder?.id && (
        <BindAuthenticationModal
          open={bindAuthModalOpen}
          omsRecycleOrderId={String(dataSource.omsRecycleOrder.id)}
          onClose={() => setBindAuthModalOpen(false)}
          onBindSuccess={getDetail}
        />
      )}
    </div>
  );
};

export default RecyclingConsignmentDetail;
